import React from "react";

const SLIDE_OUT = "mte-we-slide-out";

export function useDrawerSlide(open: boolean) {
  const [present, setPresent] = React.useState(open);
  const [closing, setClosing] = React.useState(false);
  const closingRef = React.useRef(false);

  if (open && !present && !closingRef.current) {
    setPresent(true);
  }

  React.useEffect(() => {
    if (open) {
      closingRef.current = false;
      setClosing(false);
      setPresent(true);
      return;
    }
    if (!present || closingRef.current) return;
    closingRef.current = true;
    setClosing(true);
  }, [open, present]);

  const finishClose = React.useCallback(() => {
    if (!closingRef.current) return;
    closingRef.current = false;
    setClosing(false);
    setPresent(false);
  }, []);

  React.useEffect(() => {
    if (!closing) return;
    const timer = window.setTimeout(finishClose, 1200);
    return () => window.clearTimeout(timer);
  }, [closing, finishClose]);

  const onAnimationEnd = React.useCallback(
    (event: React.AnimationEvent<HTMLElement>) => {
      if (event.target !== event.currentTarget) return;
      if (event.animationName !== SLIDE_OUT) return;
      finishClose();
    },
    [finishClose],
  );

  return { present, closing, onAnimationEnd };
}

export function drawerSlideClass(
  direction: "left" | "right" | "bottom",
  closing: boolean,
  className?: string,
) {
  return [
    className,
    "mte-we-slide",
    `mte-we-slide--${direction}`,
    closing ? "is-closing" : "",
  ]
    .filter(Boolean)
    .join(" ");
}

export function DrawerSlide({
  open,
  direction = "right",
  className,
  children,
  onAnimationEnd,
  ...rest
}: React.HTMLAttributes<HTMLDivElement> & {
  open: boolean;
  direction?: "left" | "right" | "bottom";
}) {
  const slide = useDrawerSlide(open);
  if (!slide.present) return null;
  return (
    <div
      {...rest}
      className={drawerSlideClass(direction, slide.closing, className)}
      onAnimationEnd={(event) => {
        slide.onAnimationEnd(event);
        onAnimationEnd?.(event);
      }}>
      {children}
    </div>
  );
}
